import { useState, useEffect } from 'react';

import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

import { useTranslate } from 'src/locales';

// ----------------------------------------------------------------------

type Label = { id: string; labelName: string; labelColor?: string; disabled?: boolean };

type Props = {
  labels: Label[];
  onAddRecipients: (selected: Label[]) => void;
  handleSearchLabel: (selected: string) => void;
  defultLabel: Label[];
  isLabelEdit?: boolean;
};

// helper to calculate text color based on background
export function getContrastYIQ(hexcolor: string | undefined) {
  if (!hexcolor) return '#000'; // default to black if no color
  hexcolor = hexcolor.replace('#', '');
  if (hexcolor.length === 3) {
    hexcolor = hexcolor
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const r = parseInt(hexcolor.substr(0, 2), 16);
  const g = parseInt(hexcolor.substr(2, 2), 16);
  const b = parseInt(hexcolor.substr(4, 2), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? '#000' : '#fff'; // black text on light bg, white on dark bg
}

export function ChatLabel({
  labels,
  onAddRecipients,
  defultLabel,
  handleSearchLabel,
  isLabelEdit = true,
}: Props) {
  const [selectedLabels, setSelectedLabels] = useState<Label[]>(defultLabel ?? []);
  const [searchRecipients, setSearchRecipients] = useState('');
  const { t } = useTranslate('translation');
  const theme = useTheme();

  const noLabelTextColor = theme.palette.mode === 'dark' ? '#fff' : '#000';

  // Sync default labels when they change
  useEffect(() => {
    if (defultLabel) {
      const validLabels = defultLabel.map(
        (label) => labels?.find((l) => l.id === label?.id) || label
      );
      setSelectedLabels(validLabels);
    } else {
      setSelectedLabels([]);
    }
  }, [defultLabel, labels]);

  return (
    <Autocomplete
      multiple
      freeSolo
      disabled={isLabelEdit}
      options={
        labels && labels?.length > 0
          ? labels
          : [{ id: 'no-label', labelName: t('NO_LABEL_AVAILABLE'), disabled: true }]
      }
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.labelName)}
      filterSelectedOptions
      filterOptions={(options) =>
        options.filter((option) =>
          typeof option === 'string'
            ? true
            : !selectedLabels.some((selected) => selected.id === option.id)
        )
      }
      value={selectedLabels}
      onChange={(event, newValue) => {
        const updatedValues = newValue.map((option) =>
          typeof option === 'string' ? { id: option, labelName: option } : option
        );
        setSelectedLabels(updatedValues);
        onAddRecipients(updatedValues);
      }}
      onInputChange={(event, newValue) => {
        setSearchRecipients(newValue);
        handleSearchLabel(newValue);
      }}
      renderOption={(props, option) => {
        const isNoLabel = option?.id === 'no-label' || !option?.labelColor;
        return (
          <MenuItem
            {...props}
            key={option?.id}
            disabled={option?.id === 'no-label'}
            style={{
              backgroundColor: isNoLabel ? 'transparent' : option?.labelColor,
              color: isNoLabel ? noLabelTextColor : getContrastYIQ(option?.labelColor),
              padding: '8px',
            }}
          >
            {option?.labelName}
          </MenuItem>
        );
      }}
      sx={{ mx: 1, my: 2 }}
      renderTags={(selected, getTagProps) =>
        selected.map((option, index) => {
          const isNoLabel = option?.id === 'no-label' || !option?.labelColor;
          return (
            <Chip
              {...getTagProps({ index })}
              size="small"
              variant="soft"
              label={option?.labelName}
              key={option?.id}
              sx={{
                backgroundColor: isNoLabel ? 'transparent' : option?.labelColor,
                color: isNoLabel ? noLabelTextColor : getContrastYIQ(option?.labelColor),
                border: isNoLabel ? `1px dashed ${noLabelTextColor}` : 'none',
                '&:hover': {
                  backgroundColor: isNoLabel ? 'transparent' : option?.labelColor,
                  boxShadow: 'none',
                  cursor: 'default',
                },
              }}
            />
          );
        })
      }
      renderInput={(params) => <TextField {...params} placeholder={t('ADD_LABELS')} />}
    />
  );
}
